import { sendAnalyticsEvent, trackElementInteraction, trackError, trackPagePerformance } from './analytics';
import { 
    firePageView,
    fireAddPaymentInfo, 
    firePurchaseWithEventId 
} from './pixelTracking';

/** 
 * Utilitário de rastreamento específico para o fluxo de checkout
 * Fornece funções especializadas que podem ser importadas nos componentes
 * sem necessidade de modificar diretamente o código existente
 * 
 * ATUALIZADO: Agora inclui disparo de pixels com event_id para dedupe com CAPI
 */

interface CheckoutTrackingPaymentData {
  paymentMethod: string;
  amount: number;
  installments: number;
  currency?: string;
  couponApplied?: boolean;
  couponDiscount?: number;
  hasBumps?: boolean;
  bumpQuantity?: number;
  selectedBumps?: string[];
  offerId?: string;
}

interface CheckoutErrorData {
  errorType: 'payment' | 'validation' | 'server' | 'processing';
  errorMessage?: string;
  paymentMethod?: string;
  fieldName?: string;
}

/**
 * Rastreia a visualização da página de checkout
 * Dispara apenas PageView no primeiro load
 * InitiateCheckout será disparado no blur do email ou no fallback do botão "Pagar agora"
 * @param offerId ID da oferta sendo visualizada
 * @param referrer Página de origem (se disponível)
 * @param pixels Lista de pixels configurados (opcional)
 * @param sessionId ID da sessão (opcional)
 * @param isFirstLoad Se é o primeiro carregamento da página (dispara PageView)
 */
export const trackCheckoutView = (
  offerId?: string, 
  referrer?: string,
  pixels?: any[],
  sessionId?: string,
  isFirstLoad: boolean = true
) => {
  sendAnalyticsEvent('checkout_view', {
    offer_id: offerId || 'unknown',
    checkout_step: 'landing',
    referrer: referrer || document.referrer || 'direct',
    timestamp: new Date().toISOString(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  });

  // Disparar PageView no primeiro load (ou troca de rota em SPA)
  if (isFirstLoad && pixels) {
    firePageView(pixels, sessionId);
  }

  // InitiateCheckout NÃO dispara mais aqui!
  // Será disparado no blur do email ou no fallback do botão "Pagar agora"

  // Inicia o rastreamento de desempenho da página
  trackPagePerformance();
};

/**
 * Rastreia cada etapa de preenchimento do formulário
 * @param fieldName Nome do campo preenchido
 * @param isValid Se o valor é válido
 * @param offerId ID da oferta
 */
export const trackFormFieldCompletion = (fieldName: string, isValid: boolean, offerId?: string) => {
  sendAnalyticsEvent('checkout_form_field', {
    field_name: fieldName,
    is_valid: isValid,
    offer_id: offerId || 'unknown'
  });
};

/**
 * Rastreia mudanças no método de pagamento
 * @param method Método selecionado ('card', 'pix', etc)
 * @param offerId ID da oferta
 * @param pixels Lista de pixels configurados (opcional)
 * @param sessionId ID da sessão (opcional)
 * @param amount Valor da compra (opcional)
 */
export const trackPaymentMethodChange = (
  method: string, 
  offerId?: string,
  pixels?: any[],
  sessionId?: string,
  amount?: number
) => {
  sendAnalyticsEvent('checkout_payment_method_change', {
    payment_method: method,
    offer_id: offerId || 'unknown'
  });
  
  // Disparar AddPaymentInfo pixel com event_id
  if (pixels && pixels.length > 0 && amount) {
    fireAddPaymentInfo(pixels, {
      value: amount,
      currency: 'BRL',
      content_ids: [offerId || 'unknown'],
      content_type: 'product',
      payment_method: method,
    }, sessionId);
  }
};

/**
 * Rastreia quando o usuário inicia o processo de pagamento
 * InitiateCheckout NÃO é mais disparado aqui - será disparado no blur do email
 * @param paymentData Dados relevantes do pagamento
 */
export const trackPaymentInitiated = (
  paymentData: CheckoutTrackingPaymentData
) => {
  sendAnalyticsEvent('checkout_payment_initiated', {
    payment_method: paymentData.paymentMethod,
    amount: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    installments: paymentData.installments,
    has_coupon: !!paymentData.couponApplied,
    coupon_discount: paymentData.couponDiscount || 0,
    has_bumps: !!paymentData.hasBumps,
    bump_quantity: paymentData.bumpQuantity || 0,
    selected_bumps: paymentData.selectedBumps || [],
    offer_id: paymentData.offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
  
  // InitiateCheckout NÃO dispara mais aqui!
  // Será disparado no blur do email ou no fallback do botão "Pagar agora"
  
  // Também rastreia como um evento de ecommerce para GA4
  sendAnalyticsEvent('begin_checkout', {
    value: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    items: [
      {
        item_id: paymentData.offerId,
        item_name: 'Product Checkout', 
        price: paymentData.amount,
        quantity: 1
      }
    ]
  });
};

/**
 * Rastreia quando o pagamento é concluído com sucesso
 * @param paymentData Dados relevantes do pagamento
 * @param orderId ID do pedido criado
 * @param pixels Lista de pixels configurados (opcional)
 */
export const trackPaymentSuccess = (
  paymentData: CheckoutTrackingPaymentData, 
  orderId: string,
  pixels?: any[]
) => {
  sendAnalyticsEvent('checkout_payment_success', {
    payment_method: paymentData.paymentMethod,
    amount: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    installments: paymentData.installments,
    has_coupon: !!paymentData.couponApplied,
    coupon_discount: paymentData.couponDiscount || 0,
    has_bumps: !!paymentData.hasBumps,
    bump_quantity: paymentData.bumpQuantity || 0,
    order_id: orderId,
    offer_id: paymentData.offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
  
  // Disparar Purchase pixel com event_id (dedupe com CAPI)
  if (pixels && pixels.length > 0) {
    firePurchaseWithEventId(pixels, {
      value: paymentData.amount,
      currency: paymentData.currency || 'BRL',
      content_ids: [paymentData.offerId || 'unknown'],
      content_type: 'product',
      num_items: 1,
    }, orderId);
  }
  
  // Também rastreia como um evento de purchase concluída para GA4
  sendAnalyticsEvent('purchase', {
    transaction_id: orderId,
    value: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    tax: 0,
    shipping: 0,
    items: [
      {
        item_id: paymentData.offerId,
        item_name: 'Product Purchase',
        price: paymentData.amount,
        quantity: 1
      }
    ]
  });
};

/**
 * Rastreia quando o pagamento é recusado ou falha
 * @param paymentData Dados relevantes do pagamento
 * @param reason Motivo da falha (opcional)
 */
export const trackPaymentFailed = (paymentData: CheckoutTrackingPaymentData, reason?: string) => {
  sendAnalyticsEvent('checkout_payment_failed', {
    payment_method: paymentData.paymentMethod,
    amount: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    installments: paymentData.installments,
    has_coupon: !!paymentData.couponApplied,
    failure_reason: reason || 'unknown',
    offer_id: paymentData.offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
  
  trackError('Payment Error', reason || 'Payment declined', {
    payment_method: paymentData.paymentMethod,
    amount: paymentData.amount,
    offer_id: paymentData.offerId || 'unknown'
  });
};

/**
 * Rastreia quando o usuário é redirecionado para pagamento PIX
 * @param paymentData Dados relevantes do pagamento
 * @param orderId ID do pedido criado
 */
export const trackPixRedirect = (paymentData: CheckoutTrackingPaymentData, orderId: string) => {
  sendAnalyticsEvent('checkout_pix_redirect', {
    amount: paymentData.amount,
    currency: paymentData.currency || 'BRL',
    has_coupon: !!paymentData.couponApplied,
    coupon_discount: paymentData.couponDiscount || 0,
    has_bumps: !!paymentData.hasBumps,
    bump_quantity: paymentData.bumpQuantity || 0,
    order_id: orderId,
    offer_id: paymentData.offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
};

/**
 * Rastreia quando ocorre um erro no checkout
 * @param errorData Dados do erro ocorrido
 * @param offerId ID da oferta
 */
export const trackCheckoutError = (errorData: CheckoutErrorData, offerId?: string) => {
  sendAnalyticsEvent('checkout_error', {
    error_type: errorData.errorType,
    error_message: errorData.errorMessage || 'unknown',
    payment_method: errorData.paymentMethod,
    field_name: errorData.fieldName,
    offer_id: offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
  
  trackError(`Checkout Error: ${errorData.errorType}`, errorData.errorMessage || 'unknown', {
    payment_method: errorData.paymentMethod,
    field_name: errorData.fieldName
  });
};

/**
 * Rastreia interações com os order bumps (produtos adicionais)
 * @param bumpId ID do bump
 * @param action Ação executada (add/remove)
 * @param bumpPrice Preço do bump
 * @param offerId ID da oferta principal
 */
export const trackBumpInteraction = (bumpId: string, action: 'add' | 'remove', bumpPrice?: number, offerId?: string) => {
  sendAnalyticsEvent('checkout_bump_interaction', {
    bump_id: bumpId,
    action: action,
    bump_price: bumpPrice || 0,
    offer_id: offerId || 'unknown',
    timestamp: new Date().toISOString()
  });
  
  // Rastreia como adição ao carrinho para GA4 se for adicionado
  if (action === 'add') {
    sendAnalyticsEvent('add_to_cart', {
      value: bumpPrice || 0,
      currency: 'BRL',
      items: [
        {
          item_id: bumpId,
          item_name: 'Order Bump',
          price: bumpPrice || 0,
          quantity: 1
        }
      ]
    });
  } else if (action === 'remove') {
    sendAnalyticsEvent('remove_from_cart', {
      value: bumpPrice || 0,
      currency: 'BRL',
      items: [
        {
          item_id: bumpId,
          item_name: 'Order Bump',
          price: bumpPrice || 0,
          quantity: 1
        }
      ]
    });
  }
};

/**
 * Rastreia cliques no botão de finalizar compra
 * @param offerId ID da oferta
 * @param totalAmount Valor total
 */
export const trackCheckoutButtonClick = (offerId?: string, totalAmount?: number) => {
  trackElementInteraction('checkout_button', 'click', { 
    offer_id: offerId || 'unknown',
    total_amount: totalAmount || 0,
    timestamp: new Date().toISOString()
  });
};
